"use client";

import { useState } from "react";
import Link from "next/link";

import { MarketingPricingCard } from "@/app/components/marketing/MarketingPricingCard";
import { buildDemoBookingMailto, buildPrimaryTrialCtaLabel } from "@/app/components/marketing/marketing-constants";
import { PUBLIC_ROUTE_SOUCHORY_COOKIES, PUBLIC_ROUTE_SUBDODAVATELE } from "@/app/legal/legal-meta";
import {
  ANNUAL_BILLING_DISCOUNT_PERCENT,
  PUBLIC_MONTHLY_PRICE_KC,
  PUBLIC_TRIAL_DURATION_DAYS,
} from "@/lib/billing/public-pricing";
import {
  PUBLIC_PLAN_INCLUDES,
  PUBLIC_PLAN_START_EXCLUDES,
  PUBLIC_PLAN_TAGLINE,
} from "@/lib/billing/plan-public-marketing";

const PRO_PRICING_STYLES = `
  .pro-pricing-wrapper {
    position: relative;
    border-radius: 34px;
    padding: 2px;
    background: linear-gradient(135deg, #4f46e5 0%, #8b5cf6 50%, #ec4899 100%);
  }
  .pro-pricing-inner {
    position: relative;
    background: #0a0f29;
    border-radius: 31px;
    height: 100%;
    overflow: visible;
  }
`;

export type MarketingPricingSurface = "dark" | "light";

export function MarketingPricingSection({
  surface = "dark",
  sectionId = "cenik",
  className = "",
}: {
  surface?: MarketingPricingSurface;
  sectionId?: string;
  className?: string;
}) {
  const [isAnnualPricing, setIsAnnualPricing] = useState(false);
  const isDark = surface === "dark";
  const trialDaysLabel = `${PUBLIC_TRIAL_DURATION_DAYS} dní`;
  const demoBookingMailto = buildDemoBookingMailto();
  const primaryLabel = buildPrimaryTrialCtaLabel(trialDaysLabel);

  const header = (
    <div className="mx-auto mb-10 max-w-2xl text-center">
      <h2
        className={`mb-4 font-jakarta text-3xl font-bold leading-tight md:text-5xl ${
          isDark ? "text-white" : "text-slate-950"
        }`}
      >
        Tarify Start, Pro a Team
      </h2>
      <p className={`text-base leading-relaxed md:text-lg ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        Liší se rozsah klientské zóny, AI Review PDF, Google napojení a týmový přehled. Tarif můžete kdykoli změnit.
      </p>

      <div
        role="group"
        aria-label="Fakturace"
        className={`mt-8 inline-flex rounded-full p-1 ${
          isDark ? "border border-white/10 bg-white/5" : "border border-slate-200 bg-white shadow-sm"
        }`}
      >
        <button
          type="button"
          aria-pressed={!isAnnualPricing}
          className={`min-h-[40px] rounded-full px-5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
            !isAnnualPricing
              ? "bg-indigo-600 text-white"
              : isDark
                ? "text-slate-400 hover:text-white"
                : "text-slate-600 hover:bg-slate-50"
          } ${isDark ? "focus-visible:ring-offset-[#0a0f29]" : "focus-visible:ring-offset-[#F5F3FB]"}`}
          onClick={() => setIsAnnualPricing(false)}
        >
          Měsíčně
        </button>
        <button
          type="button"
          aria-pressed={isAnnualPricing}
          className={`inline-flex min-h-[40px] items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
            isAnnualPricing
              ? "bg-indigo-600 text-white"
              : isDark
                ? "text-slate-400 hover:text-white"
                : "text-slate-600 hover:bg-slate-50"
          } ${isDark ? "focus-visible:ring-offset-[#0a0f29]" : "focus-visible:ring-offset-[#F5F3FB]"}`}
          onClick={() => setIsAnnualPricing(true)}
        >
          Ročně{" "}
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] tracking-wider ${
              isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"
            }`}
          >
            −{ANNUAL_BILLING_DISCOUNT_PERCENT}&nbsp;%
          </span>
        </button>
      </div>
    </div>
  );

  const grid = (
    <div className="grid w-full min-w-0 grid-cols-1 items-stretch gap-6 pt-2 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
      <div className="min-w-0">
      <MarketingPricingCard
        variant="dark"
        onLightSurface={!isDark}
        name="Start"
        tagline={PUBLIC_PLAN_TAGLINE.start}
        monthly={PUBLIC_MONTHLY_PRICE_KC.starter}
        annual={isAnnualPricing}
        includes={PUBLIC_PLAN_INCLUDES.start}
        excludes={PUBLIC_PLAN_START_EXCLUDES}
        primaryHref="/prihlaseni?register=1"
        primaryLabel={primaryLabel}
        showSecondaryLink
      />
      </div>
      <div className="min-w-0">
      <MarketingPricingCard
        variant="dark"
        onLightSurface={!isDark}
        featured
        name="Pro"
        tagline={PUBLIC_PLAN_TAGLINE.pro}
        monthly={PUBLIC_MONTHLY_PRICE_KC.pro}
        annual={isAnnualPricing}
        includes={PUBLIC_PLAN_INCLUDES.pro}
        primaryHref="/prihlaseni?register=1"
        primaryLabel={primaryLabel}
        showSecondaryLink
      />
      </div>
      <div className="min-w-0 sm:col-span-2 lg:col-span-1">
      <MarketingPricingCard
        variant="dark"
        onLightSurface={!isDark}
        name="Team"
        tagline={PUBLIC_PLAN_TAGLINE.scale}
        monthly={PUBLIC_MONTHLY_PRICE_KC.team}
        annual={isAnnualPricing}
        includes={PUBLIC_PLAN_INCLUDES.scale}
        primaryHref="/prihlaseni?register=1"
        primaryLabel={primaryLabel}
        showSecondaryLink
      />
      </div>
    </div>
  );

  const footer = (
    <div className="mx-auto mt-10 max-w-3xl text-center">
      <p className={`text-xs leading-relaxed ${isDark ? "text-slate-500" : "text-slate-600"}`}>
        Ceny jsou konečné za jednu organizaci v systému. Zkušební verze {trialDaysLabel} v úrovni Pro. Rozsah seatů u
        větších týmů{" "}
        <a
          href={demoBookingMailto}
          className={
            isDark
              ? "text-indigo-300 underline underline-offset-2 hover:text-white"
              : "text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
          }
        >
          doladíme na demu
        </a>
        .
      </p>
      <p className={`mt-2 text-[11px] ${isDark ? "text-slate-500" : "text-slate-500"}`}>Nejsme plátci DPH.</p>
      <p
        className={`mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] ${
          isDark ? "text-slate-600" : "text-slate-500"
        }`}
      >
        <Link
          href={PUBLIC_ROUTE_SOUCHORY_COOKIES}
          className={isDark ? "hover:text-slate-400 underline-offset-4 hover:underline" : "hover:text-slate-700 underline-offset-4 hover:underline"}
        >
          Soubory cookies
        </Link>
        <span aria-hidden>·</span>
        <Link
          href={PUBLIC_ROUTE_SUBDODAVATELE}
          className={isDark ? "hover:text-slate-400 underline-offset-4 hover:underline" : "hover:text-slate-700 underline-offset-4 hover:underline"}
        >
          Subdodavatelé
        </Link>
        <span aria-hidden>·</span>
        <Link
          href="/legal/ai-disclaimer"
          className={isDark ? "hover:text-slate-400 underline-offset-4 hover:underline" : "hover:text-slate-700 underline-offset-4 hover:underline"}
        >
          Upozornění k AI
        </Link>
      </p>
    </div>
  );

  return (
    <section
      id={sectionId}
      data-marketing-nav-tone={isDark ? "dark" : "light"}
      className={
        isDark
          ? `scroll-mt-24 overflow-x-clip border-t border-white/10 bg-[#060918] px-5 py-20 md:px-8 md:py-28 ${className}`
          : `scroll-mt-24 overflow-x-clip px-5 py-20 md:px-8 md:py-28 ${className}`
      }
    >
      <style>{PRO_PRICING_STYLES}</style>

      <div className="mx-auto w-full min-w-0 max-w-[1200px]">
        {header}
        {grid}
        {footer}
      </div>
    </section>
  );
}
